import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { APIURL } from "../../../Global";
import { MdHeight } from "react-icons/md";
import { createPortal } from "react-dom";


function EmailTemplates() {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [bodyInput, setBodyInput] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [allowedVariables, setAllowedVariables] = useState([]);
    // Autocomplete state
    const [ghostSuggestion, setGhostSuggestion] = useState(""); // the dimmed completion text
    const [currentPrefix, setCurrentPrefix] = useState("");     // what user has typed after {
    const [caretPos, setCaretPos] = useState(null);             // pixel position of caret

    // Dropdown (shown on Space/Enter)
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownItems, setDropdownItems] = useState([]);
    const [dropdownIndex, setDropdownIndex] = useState(0);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    const bodyInputRef = useRef();
    function convertBodyToDisplay(body) {
        return body.replace(/\\n/g, "\n");
    }

    function convertBodyToApi(body) {
        return body.replace(/\n/g, "\\n");
    }

    function escapeHtml(text) {
        return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    function findCurlyVariables(text) {
        const regex = /\{([^}]+)\}/g;
        let match, result = [];
        while ((match = regex.exec(text)) !== null) {
            result.push({ variable: match[1], start: match.index, end: regex.lastIndex });
        }
        return result;
    }

    // Returns caret pixel position relative to the textarea using a mirror div
    function getCaretCoordinates(textarea, position) {
        const div = document.createElement("div");
        const style = window.getComputedStyle(textarea);

        for (let prop of style) {
            div.style[prop] = style[prop];
        }

        div.style.position = "absolute";
        div.style.visibility = "hidden";
        div.style.whiteSpace = "pre-wrap";
        div.style.wordWrap = "break-word";
        div.style.top = "0px";
        div.style.left = "0px";

        div.textContent = textarea.value.substring(0, position);

        const span = document.createElement("span");
        span.textContent = "\u200b";
        div.appendChild(span);

        document.body.appendChild(div);
        const rect = span.getBoundingClientRect();
        const divRect = div.getBoundingClientRect();

        const top = rect.top - divRect.top;
        const left = rect.left - divRect.left;
        const bottom = rect.bottom - divRect.top;

        document.body.removeChild(div);

        return { top, left, bottom };
    }

    // ─── Fetch templates ──────────────────────────────────────────────────────
    useEffect(() => {
        setLoading(true);
        const str = (sessionStorage.getItem("usertoken") || "").replace(/["]+/g, "");
        axios.get(`${APIURL}/api/v1/staff/email-templates/`, {
            headers: { Authorization: "Token " + str, "Content-Type": "application/json" },
        })
            .then(res => {
                const data = res.data;
                if (data.status === "success" && Array.isArray(data.data)) {
                    setTemplates(data.data);
                    if (data.data.length > 0) handleSelect(data.data[0]);
                }
            })
            .catch(() => setTemplates([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    const handleSelect = (tmpl) => {
        const str = (sessionStorage.getItem("usertoken") || "").replace(/["]+/g, "");

        setLoading(true);

        axios.get(`${APIURL}/api/v1/staff/email-templates/?template_id=${tmpl.id}`, {
            headers: {
                Authorization: "Token " + str,
                "Content-Type": "application/json"
            },
        })
            .then(res => {
                const data = res.data;

                if (data.status === "success" && data.detail) {
                    const detail = data.detail;

                    setSelectedTemplate({
                        ...detail,
                        body: convertBodyToDisplay(detail.body)
                    });

                    setBodyInput(convertBodyToDisplay(detail.body));

                    // 👇 NEW: set allowed variables from API
                    setAllowedVariables(detail.variables || []);

                    setIsEditing(false);
                    setErrorMsg("");
                    resetAutocomplete();
                } else {
                    setErrorMsg("Failed to load template details");
                }
            })
            .catch(() => {
                setErrorMsg("Error fetching template details");
            })
            .finally(() => setLoading(false));
    };

    const resetAutocomplete = () => {
        setGhostSuggestion("");
        setCurrentPrefix("");
        setCaretPos(null);
        setShowDropdown(false);
        setDropdownItems([]);
        setDropdownIndex(0);
    };

    // ─── Insert suggestion into textarea ─────────────────────────────────────
    const insertSuggestion = useCallback((variable) => {
        const ta = bodyInputRef.current;
        if (!ta) return;
        const pos = ta.selectionStart;
        const val = bodyInput;
        const upto = val.slice(0, pos);
        const match = upto.match(/\{([a-zA-Z0-9_]*)$/);
        if (!match) return;
        const beforeMatch = upto.slice(0, upto.length - match[1].length);
        const after = val.slice(pos);
        const newVal = beforeMatch + variable + "}" + after;
        setBodyInput(newVal);
        resetAutocomplete();
        const newCaret = beforeMatch.length + variable.length + 1;
        setTimeout(() => {
            ta.focus();
            ta.selectionStart = newCaret;
            ta.selectionEnd = newCaret;
        }, 0);
    }, [bodyInput]);

    const handleBodyChange = (e) => {
        const val = e.target.value;
        const pos = e.target.selectionStart;

        setBodyInput(val);
        setErrorMsg("");
        computeAutocomplete(val, pos);
    };

    const handleKeyDown = (e) => {
        // Tab → accept ghost suggestion inline
        if (e.key === "Tab" && ghostSuggestion) {
            e.preventDefault();
            insertSuggestion(currentPrefix + ghostSuggestion);
            return;
        }
        // Escape → dismiss everything
        if (e.key === "Escape") {
            resetAutocomplete();
            return;
        }

        // Only intercept navigation keys when we have suggestions available
        const hasSuggestions = dropdownItems.length > 0;

        // ArrowDown: open dropdown if closed, or move selection down
        if (e.key === "ArrowDown" && hasSuggestions) {
            e.preventDefault();
            if (!showDropdown) {
                setShowDropdown(true);
                setDropdownIndex(0);
            } else {
                setDropdownIndex(i => Math.min(i + 1, dropdownItems.length - 1));
            }
            return;
        }

        // ArrowUp: move selection up (only when dropdown is open)
        if (e.key === "ArrowUp" && showDropdown) {
            e.preventDefault();
            setDropdownIndex(i => Math.max(i - 1, 0));
            return;
        }

        // Enter: open dropdown if closed with suggestions, or confirm selection
        if (e.key === "Enter" && hasSuggestions) {
            if (!showDropdown) {
                e.preventDefault();
                setShowDropdown(true);
                setDropdownIndex(0);
                return;
            }
            e.preventDefault();
            insertSuggestion(dropdownItems[dropdownIndex]);
            return;
        }

        // Space: open dropdown when user is inside a {var context with suggestions
        if (e.key === " " && hasSuggestions && currentPrefix !== "") {
            e.preventDefault();
            setShowDropdown(true);
            setDropdownIndex(0);
            return;
        }
    };

    // ─── Compute autocomplete state after each change ─────────────────────────
    const computeAutocomplete = (val, pos) => {
        const upto = val.slice(0, pos);
        const match = upto.match(/\{([a-zA-Z0-9_]*)$/);
        if (!match) {
            resetAutocomplete();
            return;
        }
        const prefix = match[1];
        setCurrentPrefix(prefix);

        const filtered = allowedVariables.filter(v => v.startsWith(prefix) && v !== prefix);

        // Ghost suggestion: the first match's remaining characters
        if (filtered.length > 0) {
            setGhostSuggestion(filtered[0].slice(prefix.length));
        } else {
            setGhostSuggestion("");
        }

        setDropdownItems(filtered);
        setDropdownIndex(0);

        // Store caret pixel position for dropdown placement
        if (bodyInputRef.current) {
            const textareaRect = bodyInputRef.current.getBoundingClientRect();
            const coords = getCaretCoordinates(bodyInputRef.current, pos);
            const scrollTop = bodyInputRef.current.scrollTop;
            const scrollLeft = bodyInputRef.current.scrollLeft;

            setDropdownPos({
                top: textareaRect.top + coords.bottom - scrollTop + 5,
                left: textareaRect.left + coords.left - scrollLeft,
            });
            setCaretPos({
                top: coords.top - scrollTop,
                left: coords.left - scrollLeft,
                bottom: coords.bottom - scrollTop
            });
        }
    };

    // ─── Save ─────────────────────────────────────────────────────────────────
    const handleSave = () => {
        const invalids = findCurlyVariables(bodyInput).filter(v => !allowedVariables.includes(v.variable.trim()));
        if (invalids.length > 0) {
            setErrorMsg("Only these can be used inside curly braces: " + allowedVariables.map(v => `{${v}}`).join(", "));
            return;
        }
        setErrorMsg("");
        setLoading(true);
        const str = (sessionStorage.getItem("usertoken") || "").replace(/["]+/g, "");
        const payload = { ...selectedTemplate, body: convertBodyToApi(bodyInput) };
        axios.put(`${APIURL}/api/v1/staff/email-templates/?template_id=${selectedTemplate.id}`, payload, {
            headers: { Authorization: "Token " + str, "Content-Type": "application/json" },
        })
            .then(res => {
                const data = res.data;
                if (data.status === "success" && data.message === "Email template updated successfully") {
                    axios.get(`${APIURL}/api/v1/staff/email-templates/`, {
                        headers: { Authorization: "Token " + str, "Content-Type": "application/json" },
                    }).then(res2 => {
                        const list = res2.data.data || [];
                        setTemplates(list);
                        const updated = list.find(t => t.id === selectedTemplate.id);
                        if (updated) {
                            setSelectedTemplate({ ...updated, body: convertBodyToDisplay(updated.body) });
                            setBodyInput(convertBodyToDisplay(updated.body));
                        }
                    });
                    setIsEditing(false);
                    alert("Template saved successfully.");
                } else {
                    alert("Failed to save template.");
                }
            })
            .catch(() => alert("Failed to save template."))
            .finally(() => setLoading(false));
    };

    // ─── Render body with invalid variable highlights ─────────────────────────
    const renderBodyOverlay = () => {
        let html = escapeHtml(bodyInput);
        const vars = findCurlyVariables(bodyInput);
        let offset = 0;
        vars.forEach(({ variable, start, end }) => {
            if (!allowedVariables.includes(variable.trim())) {
                const tag = `<span style="background:#ffd6d6;color:#d32f2f;">`;
                const close = `</span>`;
                html =
                    html.slice(0, start + offset) +
                    tag + html.slice(start + offset, end + offset) + close +
                    html.slice(end + offset);
                offset += tag.length + close.length;
            }
        });
        html = html.replace(/ /g, "&nbsp;");
        return <span dangerouslySetInnerHTML={{ __html: html + "\u200b" }} />;
    };

    // ─── Ghost text: rendered as an absolutely-positioned span ───────────────
    // We render it as an overlay pill right after where the caret is.
    const renderGhost = () => {
        if (!ghostSuggestion || showDropdown || !caretPos) return null;
        return (
            <div
                style={{
                    position: "absolute",
                    top: caretPos.top,
                    left: caretPos.left,
                    pointerEvents: "none",
                    zIndex: 5,
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    lineHeight: "inherit",
                    color: "rgba(120,120,120,0.55)",
                    whiteSpace: "pre",
                    userSelect: "none",
                }}
            >
                {ghostSuggestion}
            </div>
        );
    };

    // ─── Dropdown list ────────────────────────────────────────────────────────
    const renderDropdown = () => {
        if (!dropdownItems.length) return null;

        return createPortal(
            <div
                style={{
                    position: "fixed",
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    zIndex: 9999,
                    minWidth: 200,
                    maxHeight: 220,
                    overflowY: "auto",
                }}
            >
                {dropdownItems.map((v, i) => (
                    <div
                        key={v}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            insertSuggestion(v);
                        }}
                        onMouseEnter={() => setDropdownIndex(i)}
                        style={{
                            padding: "7px 14px",
                            cursor: "pointer",
                            fontFamily: "monospace",
                            background: i === dropdownIndex ? "#eee" : "white",
                        }}
                    >
                        <b>{currentPrefix}</b>
                        {v.slice(currentPrefix.length)}
                    </div>
                ))}
            </div>,
            document.body
        );
    };

    // ─── JSX ──────────────────────────────────────────────────────────────────
    return (
        <div className="container" style={{ minHeight: "71vh", paddingTop: "2%", paddingBottom: "2%" }}>
            <h3 className="title-of-page">Email Templates</h3>
            <p className="text-muted">Manage the email templates sent out by the system.</p>
            {loading && <div className="text-center my-4">Loading...</div>}
            <div className="row mt-4">
                {/* ── Sidebar list ── */}
                <div className="col-md-4">
                    <div className="list-group shadow-sm">
                        {templates.map(tmpl => (
                            <button
                                key={tmpl.id}
                                className={`list-group-item list-group-item-action ${selectedTemplate && selectedTemplate.id === tmpl.id ? "active" : ""}`}
                                onClick={() => handleSelect(tmpl)}
                                disabled={loading}
                                style={{
                                    backgroundColor: selectedTemplate && selectedTemplate.id === tmpl.id ? "rgba(85,71,159,0.9)" : "white",
                                    borderColor: selectedTemplate && selectedTemplate.id === tmpl.id ? "rgba(85,71,159,0.9)" : "#ddd",
                                    color: selectedTemplate && selectedTemplate.id === tmpl.id ? "white" : "#333",
                                    fontWeight: selectedTemplate && selectedTemplate.id === tmpl.id ? "bold" : "normal",
                                    padding: "15px 20px",
                                }}
                            >
                                {tmpl.name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Detail panel ── */}
                <div className="col-md-8">
                    <div className="card shadow-sm border-0" style={{ border: "1px solid #ddd" }}>
                        <div
                            className="card-header bg-white d-flex justify-content-between align-items-center"
                            style={{ padding: "15px 20px", borderBottom: "1px solid #eee" }}
                        >
                            <h5 className="mb-0" style={{ color: "rgba(85,71,159,0.9)", fontWeight: "bold" }}>
                                {selectedTemplate ? selectedTemplate.name : "Select a template"}
                            </h5>
                            {!isEditing && selectedTemplate && (
                                <button
                                    className="btn btn-sm px-4 py-2"
                                    style={{ backgroundColor: "rgba(85,71,159,0.9)", color: "white", borderRadius: "6px", fontWeight: "bold" }}
                                    onClick={() => setIsEditing(true)}
                                    disabled={loading}
                                >
                                    Edit Template
                                </button>
                            )}
                        </div>

                        <div className="card-body" style={{ padding: "24px" }}>
                            {isEditing && selectedTemplate ? (
                                <div>
                                    {/* Subject */}
                                    <div className="form-group mb-4">
                                        <label className="fw-bold mb-2">Email Subject</label>
                                        <input
                                            type="text"
                                            className="form-control p-2"
                                            style={{ border: "1px solid #ccc", borderRadius: "6px" }}
                                            value={selectedTemplate.subject}
                                            onChange={e => setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })}
                                            disabled={loading}
                                        />
                                    </div>

                                    {/* Body */}
                                    <div className="form-group mb-4">
                                        <label className="fw-bold mb-2">Email Body</label>

                                        {/* Hint bar */}
                                        <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: 6 }}>
                                            Type <code style={{ background: "#f5f5f5", padding: "1px 5px", borderRadius: 3 }}>{"{var"}</code> to autocomplete a variable.&nbsp;
                                            <strong>Tab</strong> accepts inline · <strong>Space/Enter</strong> opens list · <strong>Esc</strong> dismisses
                                        </div>

                                        <div style={{ position: "relative" }}>
                                            {/* Invalid variable highlight overlay */}
                                            <div
                                                aria-hidden
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    pointerEvents: "none",
                                                    color: "transparent",
                                                    background: "none",
                                                    whiteSpace: "pre-wrap",
                                                    fontFamily: "inherit",
                                                    fontSize: "inherit",
                                                    padding: "0.75rem",
                                                    overflowY: "auto",
                                                    maxHeight: 400,
                                                    zIndex: 1,
                                                    boxSizing: "border-box",
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {renderBodyOverlay()}
                                            </div>

                                            {/* Ghost text */}
                                            {renderGhost()}

                                            {/* Actual textarea */}
                                            <textarea
                                                ref={bodyInputRef}
                                                className="form-control p-3"
                                                rows={10}
                                                style={{
                                                    border: "1px solid #ccc",
                                                    borderRadius: "6px",
                                                    resize: "vertical",
                                                    maxHeight: 400,
                                                    background: "transparent",
                                                    position: "relative",
                                                    zIndex: 2,
                                                    caretColor: "#333",
                                                    minHeight: "fit-content",
                                                }}
                                                value={bodyInput}
                                                onChange={handleBodyChange}
                                                onKeyDown={handleKeyDown}
                                                onBlur={() => {
                                                    // small delay so mouseDown on dropdown can fire first
                                                    setTimeout(() => setShowDropdown(false), 150);
                                                }}
                                                onClick={e => computeAutocomplete(bodyInput, e.target.selectionStart)}
                                                disabled={loading}
                                                spellCheck={false}
                                            />

                                            {/* Dropdown list */}
                                            {renderDropdown()}
                                        </div>

                                        {errorMsg && (
                                            <div style={{ color: "#d32f2f", marginTop: 8, fontSize: "0.85rem" }}>{errorMsg}</div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary px-4 py-2"
                                            style={{ borderRadius: "6px", fontWeight: "bold" }}
                                            onClick={() => handleSelect(selectedTemplate)}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn px-4 py-2"
                                            style={{ backgroundColor: "rgba(85,71,159,0.9)", color: "white", borderRadius: "6px", fontWeight: "bold" }}
                                            onClick={handleSave}
                                            disabled={loading}
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            ) : selectedTemplate ? (
                                <div>
                                    <div className="mb-4">
                                        <h6 className="text-muted text-uppercase" style={{ fontSize: "0.85rem", letterSpacing: "1px", marginBottom: "8px" }}>Subject</h6>
                                        <div className="p-3 bg-light" style={{ border: "1px solid #e9ecef", borderRadius: "6px", fontSize: "1.05rem" }}>
                                            {selectedTemplate.subject}
                                        </div>
                                    </div>
                                    <div>
                                        <h6 className="text-muted text-uppercase" style={{ fontSize: "0.85rem", letterSpacing: "1px", marginBottom: "8px" }}>Body Content</h6>
                                        <div className="p-4 bg-light" style={{ border: "1px solid #e9ecef", borderRadius: "6px", whiteSpace: "pre-wrap", lineHeight: "1.6", fontSize: "0.95rem" }}>
                                            {selectedTemplate.body}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-muted">No template selected.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EmailTemplates;